import { Form } from "react-bootstrap";
import { useMemo, useState } from "react";
import { useDebounce, useMediaQuery } from "@reactuses/core";
import { Button } from "../../../../components/core/basic/Button";
import { DateField, InputField, Label } from "../../../../components/custom/programs/fields";
import { keepPreviousData, queryOptions, useMutation } from "@tanstack/react-query";
import { useServerTable } from "../../../../components/table/tableFeatures";
import { Table } from "../../../../components/table/Table";
import { RT8Pagination } from "../../../../components/table/Pagination";
import { useBlockWindowQuery } from "../../../../components/table/useBlockWindowQuery";
import { createColumnBuilder } from "../../../../components/table/columnBuilder";
import { ProductCell } from "../../../merchandising/components/cells/ProductCell";
import { clientToServerSorting } from "../../../../components/table/utils";
import { SearchInput } from "../../../../components/fields/base/TextField";
import api from "../../../../utils/api";
import { apiClient } from "../../../../zodios/client";
import { successToast, errorToast } from "../../../../utils/toasts";
import { useAppSelector } from "../../../../redux/hooks";
import { useForm, useWatch } from "react-hook-form";
import { SelectField, CheckboxField } from "../../../dsp/forms/fields";
import {
  AUDIENCE_LOOKALIKE_OPTIONS,
  getTemplateSpec,
  latestSelectableAudienceDay,
  MARKET_PLACE_ID_TO_COUNTRY_CODE_MAP,
  toAmcTimeWindow,
} from "../../utils/utils";
import { useFetch } from "../../../../hooks/api";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import moment from "moment";
import { Flex, HStack } from "../../../../../styled-system/jsx";
import { css } from "../../../../../styled-system/css";
import { useAudienceTemplates } from "./useAudienceTemplates";
import { useAmcAdvertisers } from "../../api/useAmcAdvertisers";
import { StringListInput } from "../../../../components/fields/base/StringListInput";
import { AsinListInput } from "../../../../components/fields/base/StringListInput.presets";
import { MultiSelect } from "../../../../components/fields/base/MultiSelect";
import { TagMultiSelect } from "../../../tags/components/TagMultiSelect";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { useSimpleCan } from "../../../../config/casl/casl";
import { useTranslation } from "react-i18next";

const INITIAL_AUDIENCE_FORM_DATA = {
  audience_name: "",
  description: "",
  tags: [],
  refresh_rate_days: 7,
  time_window_relative: true,
  time_window_start: null,
  time_window_end: latestSelectableAudienceDay(),
  lookalike_audience_expected_reach: AUDIENCE_LOOKALIKE_OPTIONS[0].value,
  views_range_lower: null,
  views_range_upper: null,
  search_term_fields: [],
};

const suggestedTags = ["Awareness", "Consideration", "Purchase Intent", "Loyalty"];

const columnHelper = createColumnBuilder();

const AudienceProductsPreview = ({ asinFilter, marketPlace }) => {
  const { t } = useTranslation(["amc"]);
  const audienceProductColumns = useMemo(
    () => [
      columnHelper.accessor("product_title", {
        id: "product_title",
        header: t(($) => $.amc["Product"]),
        minSize: 100,
        size: 350,
        cell: (props) => (
          <ProductCell
            original={{
              ...props.row.original,
              product_id: String(props.row.original.product_id),
              cellContext: "merchandising",
            }}
            value={props.getValue()}
            showInventoryBadge={false}
            className={css({ marginRight: "auto", overflowX: "hidden" })}
          />
        ),
      }),
      columnHelper.metric("sales"),
      columnHelper.metric("units"),
      columnHelper.metric("ad_sales"),
      columnHelper.metric("ad_spend"),
    ],
    [t],
  );
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState([{ id: "sales", desc: true }]);
  const [globalFilter, setGlobalFilter] = useState("");
  const debounceSearch = useDebounce(globalFilter, 500);

  const queryOptionsGetter = (params) => {
    const search = params.search ? params.search.split(",") : [];
    return queryOptions({
      queryKey: [
        {
          entity: "V4ProductReportByGrouping",
          marketPlace,
          grouping: "product_id",
          filters: params.filters,
          search,
          params,
        },
      ],
      queryFn: () =>
        apiClient.getV4ProductReportByGrouping({
          queries: {
            grouping: "product_id",
            marketplace: marketPlace,
            start_date: moment().subtract(31, "days").format("YYYY-MM-DD"),
            end_date: moment().subtract(1, "days").format("YYYY-MM-DD"),
            start_date_previous: moment().subtract(62, "days").format("YYYY-MM-DD"),
            end_date_previous: moment().subtract(32, "days").format("YYYY-MM-DD"),
            filters: params.filters,
            search,
            cursor: params.pagination.pageIndex * params.pagination.pageSize || undefined,
            limit: params.pagination.pageSize,
            ordering: clientToServerSorting(params.sorting),
          },
        }),
      placeholderData: keepPreviousData,
    });
  };

  const productQuery = useBlockWindowQuery(queryOptionsGetter, {
    pagination,
    sorting,
    search: debounceSearch,
    filters: asinFilter,
  });

  const table = useServerTable({
    data: productQuery.data?.data,
    columns: audienceProductColumns,
    rowCount: productQuery.data?.count || 0,
    state: { pagination, sorting, globalFilter },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    meta: {
      showSkeleton: productQuery.isFetching && (!productQuery.data || productQuery.isPlaceholderData),
    },
  });

  return (
    <>
      <HStack paddingY={"small"} justifyContent={"space-between"}>
        <SearchInput
          aria-label={t(($) => $.amc["Search products"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={globalFilter}
          onChange={setGlobalFilter}
        />
        <RT8Pagination table={table} />
      </HStack>
      <Table table={table} />
    </>
  );
};

const rejectionReason = (data) => {
  if (!data) return null;
  if (typeof data === "string") return data;
  if (Array.isArray(data)) return data.map(rejectionReason).filter(Boolean).join(" ");
  return Object.entries(data)
    .map(([field, detail]) => {
      const message = rejectionReason(detail);
      if (!message) return null;
      return field === "detail" ? message : `${field}: ${message}`;
    })
    .filter(Boolean)
    .join(" ");
};

const validateRequiredFields = (formState, asins, templateSpec) => {
  const {
    audience_name,
    time_window_start,
    time_window_end,
    template_name,
    advertiser_id,
    search_term_fields,
    data_set_id,
  } = formState;

  if (!audience_name || !time_window_start || !time_window_end || !template_name || !advertiser_id) {
    return false;
  }

  for (const param of templateSpec?.parameter_schema ?? []) {
    if (!param.required || param.kind !== "sql_substitution") continue;
    if ((param.name === "asins" || param.name === "tracked_item_asins") && !asins?.length) return false;
    if (param.name === "search_terms" && !search_term_fields?.length) return false;
    if (param.name === "table_name" && !data_set_id) return false;
  }

  return true;
};

const AudienceForm = ({ setShowAudienceForm, defaultFormData = {}, readOnly = false }) => {
  const { t } = useTranslation(["amc"]);
  const fmt = useDateFormatter("monthDay");
  const [selectedAsins, setSelectedAsins] = useState(defaultFormData?.asins ? defaultFormData.asins : []);

  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const mobileStyle = useMediaQuery("(max-width:1000px)");
  const { templates } = useAudienceTemplates();
  const templateOptions = useMemo(() => {
    return (
      templates?.map((audienceTemplate) => ({
        value: audienceTemplate.audience_template_name,
        label: audienceTemplate.audience_display_name,
      })) ?? []
    );
  }, [templates]);

  const validAsins = useMemo(
    () => selectedAsins?.filter((asin) => asin.length === 10 && asin.toLowerCase().startsWith("b0")),
    [selectedAsins],
  );

  const asinFilter = validAsins?.length ? [{ id: "asin", value: { op: "anyof", value: validAsins } }] : [];

  const errorClassNames = "d-block ms-3 mt-0 fs-5";

  const {
    control,
    reset,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useForm({
    mode: "onBlur",
    defaultValues: { ...INITIAL_AUDIENCE_FORM_DATA, ...defaultFormData },
  });

  const formState = useWatch({ control });
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const templateSpec = useMemo(() => {
    return getTemplateSpec(templates, formState.template_name);
  }, [formState.template_name, templates]);

  const schemaByName = useMemo(
    () => Object.fromEntries((templateSpec?.parameter_schema ?? []).map((param) => [param.name, param])),
    [templateSpec],
  );

  const asinsParam = schemaByName.tracked_item_asins ?? schemaByName.asins;
  const showAsinsField = !!asinsParam;
  const asinsFieldOptional = !asinsParam?.required;
  const showViewsRangeFields = !!schemaByName.conversion_lower_bound;
  const showSearchTermFields = !!schemaByName.search_terms;
  const showDataLinkFields = !!schemaByName.table_name;

  const { organization_id: orgId } = useAppSelector((state) => state.user);
  const { data: dataLinks } = useFetch(
    ["datalinks", orgId],
    "/amc/datalink/",
    {},
    {
      enabled: showDataLinkFields,
      select: (res) => res.data.results,
    },
  );

  const dataLinksWithStatus = useMemo(() => {
    if (!dataLinks) {
      return [];
    }
    return dataLinks?.map((dataLink) => {
      let currentUploadStatus = dataLink.upload_status?.[0]?.upload_status;
      if (dataLink.shopify_org) {
        currentUploadStatus =
          dataLink.upload_status.find((status) => status.upload_status === "SUCCESS").upload_status ||
          currentUploadStatus;
      }
      return {
        ...dataLink,
        currentUploadStatus,
      };
    });
  }, [dataLinks]);

  if (!showViewsRangeFields && (formState.views_range_lower || formState.views_range_upper)) {
    setValue("views_range_lower", null);
    setValue("views_range_upper", null);
  }

  if (!showSearchTermFields && formState.search_term_fields?.length) {
    setValue("search_term_fields", []);
  }

  if (moment(formState.time_window_start).isAfter(moment(formState.time_window_end)) && !errors.time_window_start) {
    setError("time_window_start", {
      type: "custom",
      message: t(($) => $.amc["Start date must be before {date}"], {
        date: fmt.format(moment(formState.time_window_end).toDate()),
      }),
    });
  }

  if (errors.time_window_start && moment(formState.time_window_start).isBefore(moment(formState.time_window_end))) {
    clearErrors("time_window_start");
  }
  // Ensure minimum refresh rate
  if (
    formState.refresh_rate_days > 0 &&
    formState.refresh_rate_days < 7 &&
    formState?.lookalike_audience_expected_reach &&
    !errors.refresh_rate_days
  ) {
    setError("refresh_rate_days", {
      type: "custom",
      message: t(($) => $.amc["Refresh range days must be at least 7"]),
    });
  }
  if (errors.refresh_rate_days && (formState?.refresh_rate_days >= 7 || !formState.lookalike_audience_expected_reach)) {
    clearErrors("refresh_rate_days");
  }
  if (
    selectedAsins?.length > 0 &&
    !selectedAsins?.every((asin) => asin.length === 10 && asin.toLowerCase().startsWith("b0")) &&
    !errors.asins
  ) {
    setError("asins", {
      type: "custom",
      message: t(($) => $.amc["Invalid ASIN detected. All ASINs must start with B0 and be 10 characters in length"]),
    });
  }

  if (errors.asins && selectedAsins?.every((asin) => asin.length === 10 && asin.toLowerCase().startsWith("b0"))) {
    clearErrors("asins");
  }

  const { advertisers: amcAccounts, instance: amcInstance } = useAmcAdvertisers();

  const amcAccountOptions = useMemo(() => {
    return amcAccounts.map((account) => {
      const { name, type, id, marketplaceId, currentProfile } = account;
      let formattedAdTypes = "";
      const countryCode = MARKET_PLACE_ID_TO_COUNTRY_CODE_MAP[marketplaceId];

      if (id.includes("ENTITY")) {
        formattedAdTypes = t(($) => $.amc["Sponsored Product, Sponsored Brand and Sponsored Display"]);
      }

      if (type === "DISPLAY") {
        formattedAdTypes = t(($) => $.amc["DSP"]);
      }
      return {
        value: id,
        label: `${name} - ${formattedAdTypes} (${id})${countryCode ? " (" + countryCode + ")" : ""}`,
        currentProfile,
      };
    });
  }, [amcAccounts, t]);

  const userCanCreateAmcAudiences = useSimpleCan("create", "AMCAudience");

  const createCustomAudience = useMutation({
    mutationFn: async () => {
      const {
        audience_name,
        description,
        tags,
        refresh_rate_days,
        time_window_start,
        time_window_end,
        time_window_relative,
        lookalike_audience_expected_reach,
        advertiser_id,
        views_range_lower,
        views_range_upper,
        search_term_fields,
      } = formState ?? {};
      const asins = [...new Set(validAsins)];
      const amcInstanceId = amcInstance?.id;

      const parameters = {};
      if (asinsParam && asins.length) {
        parameters[asinsParam.name] = asins;
      }
      if (showSearchTermFields && search_term_fields?.length) {
        parameters.search_terms = search_term_fields;
      }
      if (showViewsRangeFields) {
        if (views_range_lower != null && views_range_lower !== "") {
          parameters.conversion_lower_bound = Number(views_range_lower);
        }
        if (views_range_upper != null && views_range_upper !== "") {
          parameters.conversion_upper_bound = Number(views_range_upper);
        }
      }
      if (showDataLinkFields && formState.data_set_id) {
        parameters.table_name = formState.data_set_id;
      }
      if (time_window_start) {
        parameters.timeWindowStart = toAmcTimeWindow(time_window_start);
      }
      if (time_window_end) {
        parameters.timeWindowEnd = toAmcTimeWindow(time_window_end);
      }
      parameters.timeWindowRelative = time_window_relative ?? true;
      if (refresh_rate_days != null && refresh_rate_days !== "") {
        parameters.refreshRateDays = Number(refresh_rate_days);
      }
      parameters.lookalikeAudienceExpectedReach = lookalike_audience_expected_reach ?? "EXACT";

      const createAudienceData = {
        spec: { audience_name, description, advertiser_id },
        amc_instance: amcInstanceId,
        workflow_template: templateSpec?.id,
        parameters,
        tags,
      };

      return await api.post("/amc/audiencespec/", createAudienceData);
    },

    onSuccess: () => {
      successToast({
        message: t(($) => $.amc["New audience request successfully submitted"]),
      });
      setShowAudienceForm(false);
      reset();
    },

    onError: (error) => {
      const reason = rejectionReason(error.response?.data);
      errorToast({
        message:
          reason ?
            `${t(($) => $.amc["Failed to send new audience request"])}: ${reason}`
          : t(($) => $.amc["Failed to send new audience request"]),
        error,
      });
    },
  });

  return (
    <div className="pb-5 mt-5 custom-scroll-container" style={{ maxHeight: "78vh", overflowY: "auto" }}>
      {!readOnly && (
        <>
          <h2 className="fw-bold fs-1 ps-2 text-center mt-0">{t(($) => $.amc["Create a New AMC Audience"])}</h2>
          <p className="text-center fs-4 w-75 mx-auto">
            {t(
              ($) =>
                $.amc[
                  "Complete the fields below to create a new audience. Once submitted, it may takes up to 24 hours for your audience to become available through Amazon."
                ],
            )}
          </p>
        </>
      )}
      <form onSubmit={(e) => e.preventDefault()} className="audience-creation-modal-form">
        <Flex
          direction={"column"}
          gap={"large"}
          className={css({
            width: mobileStyle ? "95%" : "75%",
            margin: "auto",
          })}
        >
          <InputField
            label={t(($) => $.amc["Audience Name"])}
            fieldName="audience_name"
            errors={errors}
            control={control}
            errorClassNames={errorClassNames}
            disabled={readOnly}
            rules={{
              required: { value: true, message: t(($) => $.amc["Audience name is required"]) },
            }}
          />
          <InputField
            label={t(($) => $.amc["Description"])}
            fieldName="description"
            errors={errors}
            control={control}
            disabled={readOnly}
            errorClassNames={errorClassNames}
          />
          {tagsEnhancedEnabled ?
            <TagMultiSelect
              entityType="AUDIENCE"
              label={t(($) => $.amc["Tags (optional)"])}
              value={formState.tags ?? []}
              onChange={(tagIds) => setValue("tags", tagIds)}
              ariaLabel={t(($) => $.amc["Tags"])}
              isDisabled={readOnly}
            />
          : <MultiSelect
              isDisabled={readOnly}
              label={t(($) => $.amc["Tags (optional)"])}
              items={[...new Set([...suggestedTags, ...(formState.tags ?? [])])].map((tag) => ({
                label: tag,
                value: tag,
              }))}
              value={formState.tags ?? []}
              onChange={(keys) => setValue("tags", keys.map(String))}
              placeholder={readOnly ? readOnly : t(($) => $.amc["Add tags here..."])}
              aria-label={t(($) => $.amc["Tags"])}
            />
          }
          <SelectField
            label={t(($) => $.amc["Advertiser"])}
            options={amcAccountOptions}
            fieldName={"advertiser_id"}
            control={control}
            disabled={readOnly}
            formatOptionLabel={(option) => (
              <HStack gap="xsmall" alignItems="center">
                <span>{option.label}</span>
                {option.currentProfile ?
                  <span
                    className={css({
                      flexShrink: 0,
                      borderRadius: "full",
                      paddingX: "small",
                      paddingY: "xsmall",
                      fontSize: "small",
                      color: "white",
                      backgroundColor: "trellisPalette.3",
                      fontWeight: "bold",
                    })}
                  >
                    {t(($) => $.amc["Current Account"])}
                  </span>
                : null}
              </HStack>
            )}
          />

          <div
            className={css({
              display: "grid",
              gridTemplateColumns: mobileStyle ? "repeat(1fr)" : "repeat(3, 1fr)",
              gap: "40px",
            })}
          >
            <DateField
              label={t(($) => $.amc["Start Date"])}
              fieldName={"time_window_start"}
              control={control}
              errors={errors}
              disabled={readOnly}
              rules={{
                required: { value: true, message: t(($) => $.amc["A start date is required"]) },
              }}
              errorClassNames={errorClassNames}
              maxDate={latestSelectableAudienceDay()}
            />
            <DateField
              label={t(($) => $.amc["End Date"])}
              fieldName={"time_window_end"}
              control={control}
              errors={errors}
              disabled={readOnly}
              rules={{
                required: { value: true, message: t(($) => $.amc["A start date is required"]) },
              }}
              errorClassNames={errorClassNames}
              maxDate={latestSelectableAudienceDay()}
            />
            <InputField
              label={t(($) => $.amc["Refresh Rate Days"])}
              fieldName="refresh_rate_days"
              type={"number"}
              errors={errors}
              control={control}
              errorClassNames={errorClassNames}
              disabled={readOnly}
              rules={{
                required: { value: true, message: t(($) => $.amc["A refresh rate is required"]) },
                min: {
                  value: 0,
                  message: t(($) => $.amc["Refresh rate days must be at least 0"]),
                },
                max: {
                  value: 21,
                  message: t(($) => $.amc["Refresh rate days cannot be greater than 21"]),
                },
              }}
            />
          </div>
          <CheckboxField
            label={t(($) => $.amc["Time Window Relative"])}
            fieldName="time_window_relative"
            control={control}
            disabled={readOnly}
          />
          <SelectField
            label={t(($) => $.amc["Template"])}
            options={templateOptions}
            fieldName={"template_name"}
            control={control}
            disabled={readOnly}
          />
          <SelectField
            label={t(($) => $.amc["Lookalike Audience"])}
            options={AUDIENCE_LOOKALIKE_OPTIONS}
            fieldName={"lookalike_audience_expected_reach"}
            control={control}
            disabled={readOnly}
          />

          {showDataLinkFields && (
            <>
              <SelectField
                label={t(($) => $.amc["1st Party Data Source"])}
                disabled={readOnly}
                options={dataLinksWithStatus
                  ?.filter(
                    (dataLink) =>
                      dataLink?.currentUploadStatus && dataLink?.currentUploadStatus?.upload_status !== "FAILED",
                  )
                  ?.map((dataLink) => {
                    const statusStr =
                      (
                        dataLink?.currentUploadStatus?.upload_status === "SUCCESS" ||
                        dataLink?.currentUploadStatus === "SUCCESS"
                      ) ?
                        ""
                      : ` ${t(($) => $.amc["(In Progress)"])}`;

                    return {
                      value: dataLink.data_set_id,
                      label:
                        dataLink?.data_source === "USER_UPLOAD" ?
                          `${dataLink.name || t(($) => $.amc["N/A"])}${statusStr}`
                        : `${dataLink.shopify_org?.amazon_profile_name}${statusStr}`,
                      disabled:
                        dataLink?.currentUploadStatus !== "SUCCESS" &&
                        dataLink?.currentUploadStatus?.upload_status !== "SUCCESS",
                    };
                  })}
                fieldName={"data_set_id"}
                control={control}
              />
              {!dataLinksWithStatus.some((dataLink) => dataLink?.currentUploadStatus === "SUCCESS") && (
                <small className="d-block pt-3 text-danger">{t(($) => $.amc["No data sources available"])}</small>
              )}
            </>
          )}

          {showViewsRangeFields ?
            <Flex gap={"medium"}>
              <InputField
                label={t(($) => $.amc["Views Range Low"])}
                fieldName="views_range_lower"
                type={"number"}
                errors={errors}
                control={control}
                errorClassNames={errorClassNames}
                disabled={readOnly}
              />
              <InputField
                label={t(($) => $.amc["Views Range High"])}
                fieldName="views_range_upper"
                type={"number"}
                errors={errors}
                control={control}
                errorClassNames={errorClassNames}
                disabled={readOnly}
              />
            </Flex>
          : null}

          {showSearchTermFields ?
            <StringListInput
              label={t(($) => $.amc["Search Term Fields"])}
              value={formState.search_term_fields ?? []}
              onChange={(tags) => setValue("search_term_fields", tags)}
              placeholder={t(($) => $.amc["Add search terms..."])}
              isDisabled={readOnly}
            />
          : null}
          {showAsinsField ?
            <Form.Group className="py-4">
              <Label>
                {t(($) => $.amc["ASINs"])} <span>{asinsFieldOptional ? t(($) => $.amc["(optional)"]) : null}</span>
              </Label>
              <div style={{ overflowY: "auto" }}>
                <AsinListInput
                  value={selectedAsins ?? []}
                  onChange={(asins) => setSelectedAsins(asins)}
                  placeholder={readOnly ? readOnly : t(($) => $.amc["Enter ASINs..."])}
                  css={{ paddingBottom: "large" }}
                  label={readOnly ? readOnly : t(($) => $.amc["Add Audience ASINs"])}
                  isDisabled={readOnly}
                  bulkEdit={!readOnly}
                  bulkEditTitle={t(($) => $.amc["Audience ASINs"])}
                />
              </div>
              {errors.asins ?
                <small className={`${errorClassNames} pt-3 text-danger`}>{errors.asins.message}</small>
              : null}
              {validAsins?.length ?
                <>
                  <small className="d-block pt-4">
                    {t(($) => $.amc["* Showing product performance over the past 30 days"])}
                  </small>
                  <AudienceProductsPreview asinFilter={asinFilter} marketPlace={marketPlace} />
                </>
              : null}
            </Form.Group>
          : null}
        </Flex>
      </form>
      {!readOnly && (
        <Flex justifyContent={"center"} gap="large" className="pt-5">
          <Button variant={"secondary"} onPress={() => setShowAudienceForm(false)}>
            {t(($) => $.amc["Cancel"])}
          </Button>
          <Button
            isDisabled={
              Object.keys(errors)?.length ||
              !validateRequiredFields(formState, selectedAsins, templateSpec) ||
              !userCanCreateAmcAudiences
            }
            isPending={createCustomAudience.isPending}
            onPress={() => {
              createCustomAudience.mutate();
            }}
          >
            {t(($) => $.amc["Create"])}
          </Button>
        </Flex>
      )}
    </div>
  );
};

export default AudienceForm;
